import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { ErrorDialogErrorCategory } from '../shared/models/error-dialog.model';
import { FileItem, FileType } from '../shared/models/file-item.model';
import { KnownWorkloadReferralNames } from '../shared/models/known-workload-referral-names.model';
import { MruFileItem } from '../shared/mru/mru-file-item.model';
import { AccessTokenConsumer, AccessTokenService } from '../shared/services/access-token.service';
import { ThemingService } from '../shared/services/theming.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { UrlParser } from '../shared/utilities/url-parser.utility';

export interface ShareControl {
    file: MruFileItem;
    index: number;
    mode: ShareControlMode;
    nav?: string;
    defaultRecipients?: Array<string>;
    onGrantAccessComplete?: (success: boolean, dialogClosed?: boolean, errorOccurred?: boolean) => void;
}

export interface ShareControlEvent {
    type: ShareControlEventType;
    shareControl: ShareControl;
}

export const enum ShareControlEventType {
    Open,
    Close
}

export enum ShareControlMode {
    Share = 1,
    CopyLink = 2,
    AtMention = 3,
    LinkSettings = 4,
    ManageAccess = 5,
    Notify = 6,
    AddPeople = 7
}

interface BaseShareInfo {
    authToken: string;
    clientId: string;
    itemName?: string;
    siteSubscriptionId: string;
    webAbsoluteUrl: string;
    isODB: boolean;
    mode: number;
    organizationName: string;
    correlationId: string;
    theme?: any;
    navId?: string;
    defaultRecipients?: Array<string>;
    hasTokenProvider?: boolean;
}

interface ShareUniqueId {
    uniqueId: string;
    isFolder: boolean;
}

interface ShareListId {
    listId: string;
    listItemId: string;
}

interface ShareItemUrl {
    itemUrl: string;
}

type ShareInfoWithUniqueId = ShareUniqueId & BaseShareInfo;
type ShareInfoWithListId = ShareListId & BaseShareInfo;
type ShareInfoWithItemUrl = ShareItemUrl & BaseShareInfo;

export type ShareIdentifier = ShareUniqueId | ShareListId | ShareItemUrl;
export type ShareInfo = ShareInfoWithUniqueId | ShareInfoWithListId | ShareInfoWithItemUrl;

export type ShareControlModeString = 'share' | 'copy' | 'atMention' | 'linkSettings' | 'manageAccess' | 'notify' | 'addPeople';

@Injectable({
    providedIn: 'root'
})
export class ShareControlService implements OnDestroy {

    private readonly shareControl: Subject<ShareControlEvent>;
    private readonly themeShadesSubscription: Subscription;
    private readonly useCompliantShareApi: boolean;
    private readonly useHasTokenProvider: boolean;

    private isDarkMode: boolean;

    constructor(
        private readonly accessTokenService: AccessTokenService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly themingService: ThemingService
    ) {
        this.shareControl = new Subject<ShareControlEvent>();
        if (this.capabilitiesService.capabilities.darkModeEnabled) {
            this.themeShadesSubscription = this.themingService.darkModeSubscription.subscribe(event => {
                this.isDarkMode = event;
            });
        }
        this.useCompliantShareApi = this.capabilitiesService.capabilities.compliantShareApiEnabled;
        this.useHasTokenProvider = this.capabilitiesService.capabilities.shareTokenClaimsEnabled;
    }

    public static getShareControlModeFromString(mode?: ShareControlModeString): ShareControlMode {
        switch (mode) {
            case 'copy':
                return ShareControlMode.CopyLink;
            case 'atMention':
                return ShareControlMode.AtMention;
            case 'linkSettings':
                return ShareControlMode.LinkSettings;
            case 'manageAccess':
                return ShareControlMode.ManageAccess;
            case 'notify':
                return ShareControlMode.Notify;
            case 'addPeople':
                return ShareControlMode.AddPeople;
            default:
                return ShareControlMode.Share;
        }
    }

    public ngOnDestroy() {
        if (this.themeShadesSubscription) {
            this.themeShadesSubscription.unsubscribe();
        }
    }

    public getToken(documentUrl: string, interactive: boolean, fileType: FileType, tokenType: AccessTokenConsumer = null, claims?: Array<string>): Promise<AccessTokenResponse> {
        const parsedUrl = UrlParser.getLocation(documentUrl);
        const resourceUrl = parsedUrl ? `${parsedUrl.protocol}//${parsedUrl.hostname}` : '';

        if (tokenType === null) {
            tokenType = this.getTokenType(documentUrl);
        }

        return this.accessTokenService.getAccessToken(tokenType, false, resourceUrl, interactive, { category: ErrorDialogErrorCategory.Share, fileType }, undefined, undefined, claims);
    }

    public getTokenType(documentUrl: string): AccessTokenConsumer {
        const referral = FileItem.referralFromFileLocation(documentUrl);
        return referral === KnownWorkloadReferralNames.OneDrive ? AccessTokenConsumer.OneDrive : AccessTokenConsumer.SharePoint;
    }

    public showControl(shareControl: ShareControl) {
        this.updateNext(shareControl, ShareControlEventType.Open);
    }

    public hideControl(shareControl: ShareControl) {
        this.updateNext(shareControl, ShareControlEventType.Close);
    }

    public subscribeToOpenEvents(callback: (e: ShareControl) => void): Subscription {
        return this.shareControl.asObservable().pipe(
            filter(event => event && event.type === ShareControlEventType.Open),
            map(event => (event.shareControl))
        ).subscribe(callback);
    }

    public subscribeToCloseEvents(callback: (e: ShareControl) => void): Subscription {
        return this.shareControl.asObservable().pipe(
            filter(event => event && event.type === ShareControlEventType.Close),
            map(event => (event.shareControl))
        ).subscribe(callback);
    }

    public getDarkMode(): boolean {
        return this.isDarkMode;
    }

    public getUseCompliantShareApi(): boolean {
        return this.useCompliantShareApi;
    }

    public getUseHasTokenProvider(): boolean {
        return this.useHasTokenProvider;
    }

    private updateNext(shareControl: ShareControl, type: ShareControlEventType) {
        if (!shareControl || !shareControl.file || shareControl.index === null || shareControl.index === undefined) {
            return;
        }
        this.shareControl.next({
            type,
            shareControl
        });
    }
}
